import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Shows a small "Install Upper Circuit Stocks as App" banner
 * on mobile when PWA install criteria are met. Hides automatically
 * after install or dismiss (remembers dismissal for 7 days in localStorage).
 */
const InstallPwaPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check dismissal cooldown (7 days)
    const dismissedAt = localStorage.getItem('pwa_install_dismissed_at');
    if (dismissedAt) {
      const ageMs = Date.now() - parseInt(dismissedAt, 10);
      if (ageMs < 7 * 24 * 60 * 60 * 1000) return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Already installed check
    window.addEventListener('appinstalled', () => {
      setVisible(false);
      localStorage.setItem('pwa_installed', '1');
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferred(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('pwa_install_dismissed_at', String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:max-w-sm z-[70] bg-gradient-to-r from-golden to-amber-500 p-[1px] rounded-2xl shadow-2xl animate-in slide-in-from-bottom"
      data-testid="install-pwa-prompt"
    >
      <div className="bg-black rounded-2xl px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-golden/20 border border-golden/40 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-golden" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-semibold leading-tight">Install Upper Circuit Stocks</div>
          <div className="text-gray-400 text-xs mt-0.5">Faster access · Offline ready · Like an app</div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleInstall}
            className="bg-golden hover:bg-golden/90 text-black font-bold text-xs px-3 py-2 rounded-lg"
            data-testid="install-pwa-btn"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-300 p-1.5"
            data-testid="install-pwa-dismiss"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPwaPrompt;
